import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { useElasticQuery } from "@/api/mutations";
import LoadingSmall from "@/components/LoadingSmall";
import Table from "@/components/Table";
import useGetColours from "@/helpers/getColours";
import { buildFilters, twoDp } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import { indexReducedPattern } from "./constants";
import SongReveal from "./Songs/_songReveal";

function SongsCellEarnings({ row: { original } }) {
  const currencySymbol = useAppStore(s => s.tenantCurrencySymbol);
  return (
    <span>
      {`${currencySymbol}${twoDp(original?.SongValues?.value)}`}
    </span>
  );
}

function SongsExpanderHeader() {
  return null;
}

function SongsExpanderCell({ row }) {
  return (
    <span>
      {row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
    </span>
  );
}

function SongsSection({ matchAccounts, dateType }) {
  const { t } = useTranslation("other");
  const [pending, setPending] = useState(true);
  const [result, setResult] = useState(null);
  const flags = useFlags();

  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { highlightColour } = useGetColours();

  const range = useAppStore(s => s.range);

  const query = {
    index: indexReducedPattern,
    body: {
      query: {
        bool: {
          filter: [
            {
              range: { [dateType]: range[dateType] },
            },
            ...buildFilters(flags, matchAccounts),

          ],
        },
      },
      aggs: {
        Songs: {
          terms: {
            field: "songCode",
            size: 500,
            order: {
              SongValues: "desc",
            },
            missing: "N/A",
          },
          aggs: {
            SongValues: {
              sum: {
                field: "ownerWriterPayment",
              },
            },
            SongTitle: {
              terms: {
                field: "songTitle.keyword",
                size: 1,
              },
            },
            WriterName: {
              top_hits: {
                size: 1,
                _source: {
                  includes: ["workWriterLastNames"],
                },
              },
            },

          },
        },
      },
    },
    size: 0,
  };

  const elasticQueryMutation = useElasticQuery();
  useEffect(() => {
    setPending(true);

    elasticQueryMutation.mutate(query, {
      onSuccess: (data) => {
        setPending(false);
        setResult(data);
      },
      onError: (error) => {
        console.log(error);
        setPending(false);
        setResult(null);
      },
    });
  }, [range, dateType]);

  const columns = React.useMemo(
    () => [
      {
        Header: t("other:song-title"),
        accessor: row => `${row?.SongTitle?.buckets?.[0].key}`,
        width: "25%",
      },
      {
        Header: t("other:_writers"),
        accessor: row => `${row?.WriterName?.hits?.hits?.[0]._source?.workWriterLastNames?.replace(/,/g, ", ")}`,
        width: "25%",
      },
      {
        Header: t("other:earnings2"),
        accessor: r => r.SongValues.value,
        Cell: SongsCellEarnings,
        width: "20%",
      },
      {
        // Make an expander cell
        Header: SongsExpanderHeader,
        id: "expander", // It needs an ID
        width: "5%",

        Cell: SongsExpanderCell,
        // We can override the cell renderer with a SubCell to be used with an expanded row
        SubCell: () => null, // No expander on an expanded row
      },

    ],
    [],
  );

  if (!pending && result) {
    return (

      <Table
        data={result?.body?.aggregations?.Songs?.buckets || []}
        columns={columns}
        highlightColour={highlightColour}
        revealComponent={(
          <SongReveal dateType={dateType} matchAccounts={matchAccounts} />
        )}
        searchSuffix={t("common:songs")}
        disableSearch
        initialSort={[{
          id: "Earnings",
          desc: true,
        }]}
        bgClass={`${isTunecore ? "bg-tunecore-dark-element" : "bg-white"} rounded-lg p-0`}
      />
    );
  }
  return (
    <div className="p-4">
      <LoadingSmall />

    </div>
  );
}

export default SongsSection;
